import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Download, AlertTriangle } from "lucide-react";
import { useMyWorkerRecord, useMyTimesheets, useMyStats, useMyBills } from "@/hooks/useWorkerPortal";
import { useIsMobile } from "@/hooks/use-mobile";
import { format } from "date-fns";

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const THRESHOLD_1099 = 600;

const timesheetStatusBadge = (status: string) => {
  switch (status) {
    case "client_approved": return <Badge className="text-[10px] bg-primary/15 text-primary border-primary/30">Paid</Badge>;
    case "submitted": return <Badge variant="secondary" className="text-[10px]">Pending</Badge>;
    default: return <Badge variant="outline" className="text-[10px]">{status}</Badge>;
  }
};

const billStatusBadge = (status: string) => {
  switch (status) {
    case "draft":
      return <Badge variant="outline" className="text-[10px] bg-gray-100 text-gray-700 border-gray-300">Draft</Badge>;
    case "pending":
      return <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300">Pending Review</Badge>;
    case "approved":
      return <Badge className="text-[10px] bg-green-100 text-green-800 border-green-300">Approved</Badge>;
    case "paid":
      return <Badge className="text-[10px] bg-blue-100 text-blue-800 border-blue-300">Paid</Badge>;
    case "voided":
      return <Badge variant="destructive" className="text-[10px]">Voided</Badge>;
    default:
      return <Badge variant="outline" className="text-[10px]">{status}</Badge>;
  }
};

const WorkerPayments = () => {
  const { data: worker, isLoading: wl } = useMyWorkerRecord();
  const { data: lines = [], isLoading: ll } = useMyTimesheets(worker?.id);
  const stats = useMyStats(worker?.id);
  const { data: bills = [], isLoading: bl } = useMyBills(worker?.vendor_id || undefined);
  const isMobile = useIsMobile();

  // Group timesheets by timesheet
  const byTs = new Map<string, any[]>();
  for (const l of lines) {
    const key = l.timesheet?.id || l.id;
    if (!byTs.has(key)) byTs.set(key, []);
    byTs.get(key)!.push(l);
  }

  const payments = Array.from(byTs.entries()).map(([id, tsLines]) => {
    const ts = tsLines[0]?.timesheet;
    return {
      id,
      weekEnding: ts?.week_ending,
      project: ts?.personnel_project?.project_number || "—",
      status: ts?.status || "draft",
      gross: tsLines.reduce((s: number, l: any) => s + (l.total_billed || 0), 0),
    };
  });

  const totalPaid = payments.filter(p => p.status === "client_approved").reduce((s, p) => s + p.gross, 0);
  const totalPending = payments.filter(p => p.status !== "client_approved").reduce((s, p) => s + p.gross, 0);
  const pct1099 = Math.min(100, (stats.ytdEarned / THRESHOLD_1099) * 100);

  const voidedBills = bills.filter((b: any) => b.status === "voided");

  if (wl || ll || bl) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">My Payments</h1>
        <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1" /> Download Summary</Button>
      </div>

      {voidedBills.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-semibold">
              {voidedBills.length} bill{voidedBills.length > 1 ? "s" : ""} voided
            </p>
            {voidedBills.slice(0, 3).map((b: any) => (
              <p key={b.id} className="text-sm mt-1">
                Bill #{b.bill_number}: {(b as any).void_reason || "No reason provided"}
              </p>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* Bills Section */}
      {bills.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Bills</CardTitle></CardHeader>
          <CardContent>
            {isMobile ? (
              <div className="space-y-3">
                {bills.map((b: any) => (
                  <div key={b.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{b.bill_number}</span>
                      {billStatusBadge(b.status)}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {b.work_orders ? `WO-${b.work_orders.number}` : "—"}
                      </span>
                      <span className="text-muted-foreground">
                        {b.bill_date ? format(new Date(b.bill_date), "MMM d, yyyy") : "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold">{fmt(b.total || 0)}</span>
                    </div>
                    {b.status === "voided" && (b as any).void_reason && (
                      <p className="text-xs text-destructive">{(b as any).void_reason}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bill #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Work Order</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bills.map((b: any) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">{b.bill_number}</TableCell>
                      <TableCell>{b.bill_date ? format(new Date(b.bill_date), "MMM d, yyyy") : "—"}</TableCell>
                      <TableCell className="text-sm">
                        {b.work_orders ? `WO-${b.work_orders.number}` : "—"}
                      </TableCell>
                      <TableCell className="text-right font-medium">{fmt(b.total || 0)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {billStatusBadge(b.status)}
                          {b.status === "voided" && (b as any).void_reason && (
                            <span className="text-[10px] text-destructive">{(b as any).void_reason}</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Timesheet Payment History */}
      <Card>
        <CardHeader><CardTitle className="text-base">Timesheet Payment History</CardTitle></CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No payment history yet</p>
          ) : isMobile ? (
            <div className="space-y-3">
              {payments.map((p) => (
                <div key={p.id} className="border rounded-lg p-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{p.project}</span>
                    {timesheetStatusBadge(p.status)}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {p.weekEnding ? `Week ending ${format(new Date(p.weekEnding), "MMM d")}` : "—"}
                    </span>
                    <span className="font-semibold">{fmt(p.gross)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.project}</TableCell>
                    <TableCell>{p.weekEnding ? `Week ending ${format(new Date(p.weekEnding), "MMM d")}` : "—"}</TableCell>
                    <TableCell className="text-right font-medium">{fmt(p.gross)}</TableCell>
                    <TableCell>{timesheetStatusBadge(p.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">YTD Summary</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><p className="text-sm text-muted-foreground">Total Earned</p><p className="text-lg font-bold">{fmt(stats.ytdEarned)}</p></div>
            <div><p className="text-sm text-muted-foreground">Total Paid</p><p className="text-lg font-bold">{fmt(totalPaid)}</p></div>
            <div><p className="text-sm text-muted-foreground">Pending</p><p className="text-lg font-bold">{fmt(totalPending)}</p></div>
            <div>
              <p className="text-sm text-muted-foreground">1099 Status</p>
              {stats.ytdEarned >= THRESHOLD_1099 ? (
                <p className="text-sm font-medium text-primary">Will receive 1099</p>
              ) : (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{fmt(stats.ytdEarned)} of {fmt(THRESHOLD_1099)}</p>
                  <Progress value={pct1099} className="h-2" />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkerPayments;
